import {IStorageService} from '../../services/storage/StorageService';
import {StorageItem, UploadResult} from '../../../shared/types/storage';

// Files are persisted as base64 data URLs in localStorage, one key per path, so
// uploads survive a page reload exactly like real S3 objects would. Each entry is
// stored under its own key (rather than a single JSON blob) to avoid rewriting the
// whole store — and the running blob — on every upload.
const FILE_KEY_PREFIX = 'lgc-mock-file:';

const storageKey = (path: string) => `${FILE_KEY_PREFIX}${path}`;

const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(reader.error ?? new Error('failed to read file'));
        reader.readAsDataURL(file);
    });

/**
 * Fully offline storage service for the mock backend. Persists uploaded files as
 * base64 data URLs in localStorage and serves them back through getSignedUrl, so
 * task-report photos, avatars and editor images all render after a reload without
 * any real S3/network access.
 */
export function createMockStorageService(): IStorageService {
    return {
        async upload(file: File, path: string): Promise<UploadResult> {
            const dataUrl = await readFileAsDataUrl(file);
            try {
                window.localStorage.setItem(storageKey(path), dataUrl);
            } catch (error) {
                // Quota exceeded — the upload still "succeeds" so the flow continues;
                // the image just won't be retrievable on reload.
                console.warn(`[mock-backend] could not persist uploaded file "${path}" to localStorage`, error);
            }
            return {path, url: dataUrl, size: file.size, etag: ''};
        },

        async download(path: string): Promise<Blob> {
            const dataUrl = window.localStorage.getItem(storageKey(path));
            if (!dataUrl) {
                return new Blob();
            }
            const response = await fetch(dataUrl);
            return response.blob();
        },

        async getSignedUrl(path: string): Promise<string> {
            return window.localStorage.getItem(storageKey(path)) ?? '';
        },

        async delete(path: string): Promise<void> {
            window.localStorage.removeItem(storageKey(path));
        },

        async list(prefix: string): Promise<StorageItem[]> {
            const items: StorageItem[] = [];
            for (let i = 0; i < window.localStorage.length; i++) {
                const key = window.localStorage.key(i);
                if (!key || !key.startsWith(FILE_KEY_PREFIX)) {
                    continue;
                }
                const path = key.slice(FILE_KEY_PREFIX.length);
                if (path.startsWith(prefix)) {
                    items.push({path, size: 0, lastModified: new Date(0), etag: ''});
                }
            }
            return items;
        },
    };
}
